import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatHelp({
  method,
  value,
}: {
  method: "currency" | "number";
  value: number;
}) {
  switch (method) {
    case "currency":
      return new Intl.NumberFormat("en-US", { style: "decimal" }).format(value);
    case "number":
      return new Intl.NumberFormat("en-US", { style: "decimal" }).format(value);
    default:
      return Number(value).toLocaleString();
  }
}

/**
 * @description: This function generates the URL to navigate within the application,
 *               forwarding relevant query parameters (UTM, gclid, fbclid) from the current URL.
 * @param {string} sourceUrl - The base source URL
 * @param {string} [applicationNavigation] - Optional navigation override URL for sub-pages
 * @return {string} - Returns the URL to navigate to, including forwarded parameters if they exist.
 */
export function navigateToApplication(
  sourceUrl: string,
  applicationNavigation = ""
): string {
  // Ensure consistent formatting by removing leading '/'
  if (applicationNavigation.startsWith("/")) {
    applicationNavigation = applicationNavigation.slice(1);
  }

  // Construct the base URL
  let baseUrl: string;
  if (applicationNavigation && typeof applicationNavigation === "string") {
    baseUrl = `${sourceUrl}/${applicationNavigation}`;
  } else {
    // Default navigation
    baseUrl = `${sourceUrl}/application`;
  }

  // Check if running in a browser environment with location search available
  if (
    typeof window !== "undefined" &&
    window.location &&
    window.location.search
  ) {
    const currentParams = new URLSearchParams(window.location.search);
    const paramsToForward = new URLSearchParams();
    const paramKeys = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "gclid",
      "fbclid",
      "custom_reference_id",
    ];

    paramKeys.forEach((key) => {
      if (currentParams.has(key)) {
        paramsToForward.set(key, currentParams.get(key)!);
      }
    });

    const forwardString = paramsToForward.toString();
    if (forwardString) {
      const separator = baseUrl.includes("?") ? "&" : "?";
      return `${baseUrl}${separator}${forwardString}`;
    }
  }

  return baseUrl;
}
